import { useInfiniteQuery, useMutation, useQueryClient } from 'react-query';
import { MEMBERS } from './utils/constants';
import { getMembers } from '../api/queries/members';
import {
  inviteMember,
  updateMemberRoleAccess,
  removeMember,
  resendInvite,
  cancelInvite,
} from '../api/mutations/members';

/**
 * ROAR-4969 (ROAR-4802 / P1.11, PR2) — the Members-pane listing query (AC7, AC8).
 *
 * Paging convention mirrors `useLoginActivity` exactly: an explicit `has_more` from the backend
 * always wins; otherwise a short (or empty) page means the end has been reached.
 */
export const useMembers = (options = {}) => {
  const { pageSize = 25, searchKey } = options;

  return useInfiniteQuery(
    [MEMBERS, { pageSize, searchKey }],
    ({ pageParam = 1 }) => getMembers({ page: pageParam, pageSize, searchKey }),
    {
      getNextPageParam: (lastPage, allPages) => {
        if (lastPage?.has_more !== undefined && lastPage?.has_more !== null) {
          return lastPage.has_more ? (allPages?.length || 1) + 1 : undefined;
        }
        const items = lastPage?.items || [];
        if (items.length < pageSize) return undefined;
        return (allPages?.length || 1) + 1;
      },
      keepPreviousData: true,
    }
  );
};

/**
 * ROAR-4970 (ROAR-4802 / P1.11, PR3) — Invite Teammate (AC1, AC10, AC15). Invalidates the
 * Members list on success so a newly-created invite shows up immediately; a refusal (seat cap,
 * non-leaking conflict, last-write-admin, etc.) rejects with the axios error intact so the
 * caller can display `error.response.data.message` verbatim.
 */
export const useInviteMember = (options = {}) => {
  const queryClient = useQueryClient();
  return useMutation(inviteMember, {
    onSuccess: () => queryClient.invalidateQueries(MEMBERS),
    ...options,
  });
};

/**
 * ROAR-4970 (ROAR-4802 / P1.11, PR3) — Edit a member's role/access (AC2, AC3).
 */
export const useUpdateMemberRoleAccess = (options = {}) => {
  const queryClient = useQueryClient();
  return useMutation(updateMemberRoleAccess, {
    onSuccess: () => queryClient.invalidateQueries(MEMBERS),
    ...options,
  });
};

/**
 * ROAR-4970 (ROAR-4802 / P1.11, PR3) — Remove a member (AC2, AC3).
 */
export const useRemoveMember = (options = {}) => {
  const queryClient = useQueryClient();
  return useMutation(removeMember, {
    onSuccess: () => queryClient.invalidateQueries(MEMBERS),
    ...options,
  });
};

/**
 * ROAR-4802 follow-up (AC10, AC13) — Re-Invite a pending invite, unblocked by the backend's
 * `invite_id` addition to GET /team/members. Invalidates the list so a bumped `sent_count`
 * (not currently surfaced) and any 409 (invite no longer pending) reflect promptly.
 */
export const useResendInvite = (options = {}) => {
  const queryClient = useQueryClient();
  return useMutation(resendInvite, {
    onSuccess: () => queryClient.invalidateQueries(MEMBERS),
    ...options,
  });
};

/**
 * ROAR-4802 follow-up (AC13) — Cancel a pending invite. Invalidates the list: the cancelled row
 * loses its `invite_id` on refetch (see cancelInvite's own doc for the known status-field gap
 * this surfaces, not fixes).
 */
export const useCancelInvite = (options = {}) => {
  const queryClient = useQueryClient();
  return useMutation(cancelInvite, {
    onSuccess: () => queryClient.invalidateQueries(MEMBERS),
    ...options,
  });
};
